import { useEffect, useState, useCallback, type FC } from 'react';
import { createPortal } from 'react-dom';
import TourStep, { type TourStepData } from './TourStep';

interface GuidedTourProps {
  tourId: string;
  steps: TourStepData[];
  isActive: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

const SPOTLIGHT_PADDING = 8;

/**
 * Overlay sombre + spotlight sur l'element cible + tooltip.
 * Pas de librairie externe — controle total du design.
 */
export const GuidedTour: FC<GuidedTourProps> = ({ steps, isActive, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);

  const step = steps[currentStep];

  // Calcul du spotlight
  const updateSpotlight = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (!el) {
      setSpotlightRect(null);
      return;
    }
    // Scroll vers l'element si hors viewport
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Petit delai pour le scroll
    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      setSpotlightRect(rect);
    });
  }, [step]);

  useEffect(() => {
    if (!isActive) return;
    updateSpotlight();

    const onResize = () => updateSpotlight();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [isActive, currentStep, updateSpotlight]);

  // Reset quand le tour s'active
  useEffect(() => {
    if (isActive) setCurrentStep(0);
  }, [isActive]);

  const handleNext = () => {
    if (currentStep >= steps.length - 1) {
      onComplete();
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  if (!isActive || !step) return null;

  const overlay = (
    <div className="fixed inset-0 z-[10000]">
      {/* Overlay SVG avec trou */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spotlightRect && (
              <rect
                x={spotlightRect.left - SPOTLIGHT_PADDING}
                y={spotlightRect.top - SPOTLIGHT_PADDING}
                width={spotlightRect.width + SPOTLIGHT_PADDING * 2}
                height={spotlightRect.height + SPOTLIGHT_PADDING * 2}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#spotlight-mask)"
          style={{ pointerEvents: 'auto' }}
          onClick={onSkip}
        />
      </svg>

      {/* Spotlight highlight ring */}
      {spotlightRect && (
        <div
          className="absolute rounded-lg ring-2 ring-blue-400 dark:ring-blue-500 ring-offset-2 ring-offset-transparent pointer-events-none transition-all duration-300"
          style={{
            top: spotlightRect.top - SPOTLIGHT_PADDING,
            left: spotlightRect.left - SPOTLIGHT_PADDING,
            width: spotlightRect.width + SPOTLIGHT_PADDING * 2,
            height: spotlightRect.height + SPOTLIGHT_PADDING * 2,
          }}
        />
      )}

      {/* Tooltip */}
      <TourStep
        step={step}
        current={currentStep}
        total={steps.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={onSkip}
      />
    </div>
  );

  return createPortal(overlay, document.body);
};

export default GuidedTour;
